import confetti from 'canvas-confetti';

export type CelebrationAnimationType = 'confetti' | 'fireworks' | 'balloons' | 'text_burst' | 'explosion';

export const CELEBRATION_ANIMATION_TYPES: CelebrationAnimationType[] = [
  'confetti', 'fireworks', 'balloons', 'text_burst', 'explosion',
];

/**
 * Las 5 animaciones reales de M25.1, en un solo sitio para que
 * {@code CelebrationOverlayComponent} (el disparo real, por SSE) y la "Vista previa" del modal de
 * configuración toquen exactamente la misma función — lo que se previsualiza es lo que se ve.
 *
 * <p><b>Por qué "fireworks" cambió de un bucle por frame a ráfagas por intervalo.</b> La versión
 * anterior disparaba dos ráfagas de 60 partículas en CADA frame de {@code requestAnimationFrame}
 * durante ~2.5s — a 60fps eso son cientos de ráfagas y decenas de miles de partículas vivas a la
 * vez, que es exactamente el motivo por el que se sentía lento. Ahora dispara una ráfaga cada
 * ~350ms (7-8 ráfagas en total), que es lo que de verdad se lee como "fuegos artificiales" sin
 * saturar el canvas.
 *
 * <p><b>Por qué "estrellas"/"globos" salen de varios puntos y no de uno solo.</b> Cubrir toda la
 * pantalla sin afectar el rendimiento no es "más partículas", es la MISMA cantidad repartida en
 * varios orígenes en vez de uno: {@link scatteredBurst} reparte el mismo total de partículas entre
 * N puntos al azar de la pantalla, así que el presupuesto de partículas vivas a la vez no cambia.
 */
export function playCelebrationAnimation(fire: confetti.CreateTypes, type: CelebrationAnimationType): void {
  switch (type) {
    case 'fireworks':
      fireworksBurst(fire);
      break;
    case 'balloons':
      balloonsBurst(fire);
      break;
    case 'text_burst':
      textBurst(fire);
      break;
    case 'explosion':
      explosionBurst(fire);
      break;
    default:
      fire({ particleCount: 140, spread: 90, origin: { y: 0.6 } });
      bigText(fire, '¡Bien hecho!', '#7c3aed');
  }
}

function fireworksBurst(fire: confetti.CreateTypes): void {
  let ticks = 0;
  const maxTicks = 8;
  const next = () => {
    // Tercer origen al centro además de los dos bordes — misma cantidad total por tanda (90) que
    // antes, solo repartida entre 3 puntos en vez de 2, para que se vea por toda la pantalla.
    fire({ particleCount: 30, angle: 60, spread: 60, startVelocity: 45, origin: { x: 0, y: 0.6 } });
    fire({ particleCount: 30, angle: 90, spread: 70, startVelocity: 45, origin: { x: 0.5, y: 0.45 } });
    fire({ particleCount: 30, angle: 120, spread: 60, startVelocity: 45, origin: { x: 1, y: 0.6 } });
    ticks++;
    if (ticks < maxTicks) setTimeout(next, 350);
  };
  next();
  bigText(fire, '¡Excelente!', '#f97316');
}

/**
 * Círculos grandes, poca gravedad y velocidad inicial hacia arriba — se leen como globos que
 * flotan. Suben desde varios puntos del borde inferior (no solo el centro) para cubrir todo el
 * ancho de la pantalla, con el mismo total de 24 partículas de siempre repartido entre esos
 * puntos — no hay más partículas vivas a la vez, solo más dispersas.
 */
function balloonsBurst(fire: confetti.CreateTypes): void {
  const colors = ['#ef4444', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6'];
  const xPositions = [0.1, 0.35, 0.65, 0.9];
  for (const x of xPositions) {
    fire({
      particleCount: 6, spread: 70, angle: 90, startVelocity: randomBetween(20, 30), gravity: 0.25, decay: 0.94,
      scalar: 2.2, ticks: 300, shapes: ['circle'], colors, origin: { x, y: 1 },
    });
  }
  bigText(fire, '¡Lo lograste!', '#ec4899');
}

/**
 * UNA sola palabra grande en el centro, no una lluvia de partículas — es lo que pide esta
 * animación ("texto de felicitación"), a diferencia de las otras 4 que sí son ráfagas de
 * partículas encima de las cuales también se agregó su propio texto (ver {@link bigText}).
 */
function textBurst(fire: confetti.CreateTypes): void {
  bigText(fire, '¡Felicidades!', '#2563eb');
  // Estrellitas sueltas por TODA la pantalla (ver Javadoc de scatteredBurst), no amontonadas en
  // el centro — mismo total de 36 partículas de siempre, repartidas en 6 orígenes al azar.
  scatteredBurst(fire, 36, 6, { shapes: ['star'], spread: 360, gravity: 0.9, decay: 0.9, ticks: 200 });
}

/** Ráfaga radial (spread 360) con estrellas por toda la pantalla — mismo total de siempre (160), repartido en varios orígenes. */
function explosionBurst(fire: confetti.CreateTypes): void {
  scatteredBurst(fire, 160, 4, { spread: 360, startVelocity: 55, decay: 0.9, scalar: 1.1, shapes: ['star'] });
  bigText(fire, '¡Increíble!', '#dc2626');
}

/**
 * Reparte `totalParticles` entre `origins` puntos al azar de la pantalla (evitando los bordes
 * extremos, donde la mitad de la partícula nacería fuera de la vista) — cubre toda la pantalla sin
 * subir el total de partículas vivas a la vez, que es lo que de verdad pesa en el rendimiento.
 */
function scatteredBurst(fire: confetti.CreateTypes, totalParticles: number, origins: number, options: Partial<confetti.Options>): void {
  const perOrigin = Math.round(totalParticles / origins);
  for (let i = 0; i < origins; i++) {
    fire({
      ...options,
      particleCount: perOrigin,
      startVelocity: options.startVelocity ?? randomBetween(20, 45),
      origin: { x: randomBetween(0.08, 0.92), y: randomBetween(0.15, 0.7) },
    });
  }
}

/**
 * UNA sola palabra grande y con diseño (no una lluvia de partículas), usada por las 5 animaciones
 * con su propio texto y su propio color — el mismo recurso que ya se armó para "texto de
 * felicitación", ahora compartido. `particleCount: 1` + `scalar` enorme la vuelve legible;
 * `flat: true` (opción real de canvas-confetti) evita que el texto rote en 3D y se vea deformado;
 * `startVelocity`/`gravity` bajos hacen que suba apenas un poco y flote antes de caer, en vez de
 * salir disparada. `color` va en `shapeFromText`, que es quien de verdad pinta el texto — pasarlo
 * en las opciones de `fire()` no tiene efecto sobre una forma ya rasterizada.
 */
function bigText(fire: confetti.CreateTypes, text: string, color: string): void {
  const textShape = [confetti.shapeFromText({ text, scalar: 12, color })];
  fire({
    particleCount: 1,
    shapes: textShape,
    scalar: 7,
    flat: true,
    spread: 0,
    startVelocity: 8,
    gravity: 0.2,
    decay: 0.96,
    ticks: 260,
    origin: { x: 0.5, y: 0.45 },
  });
}

function randomBetween(min: number, max: number): number {
  return min + Math.random() * (max - min);
}
